<script lang="ts">
  /**
   * The estate's app bar — BirdJournal.SupportSite's `AppBar.astro`, which is itself the blog's header.
   *
   * The three sites are linked from each other and reached from the same emails and search results, so
   * this bar has to be the same object on all of them: the same frosted white, the same 69px, the same
   * round 36px logo and two-tone wordmark, the same `birdjournal.com` escape hatch, and the same
   * right-hand account cluster. It is on EVERY page here including sign-in, because arriving from
   * `support.` at a page with no bar is exactly the seam this is meant to remove.
   *
   * WHAT DIFFERS, AND WHY. The support site puts its four sections in this bar; the account sections
   * stay in the sidebar instead. Their labels are "Marketing preferences" and "Request account
   * deletion" — five of them run to roughly a thousand pixels, where support's four one-word labels fit
   * in a couple of hundred. Forcing them up here would wrap the bar on a laptop and abbreviate the
   * labels into something less clear than what the Website already says. So the bar carries the estate
   * identity and the sidebar carries the sections.
   */
  import { session } from '../session.svelte'
  import { router } from '../router.svelte'
  import { api } from '../api'
  import { config } from '../config'
  import { ripple } from '../ripple'
  import { icons } from '../icons'
  import { YOUR_ACCOUNT_LABEL } from '../avatar'
  import Avatar from './Avatar.svelte'

  let open = $state(false)
  let root = $state<HTMLElement | undefined>()

  // The name the account publishes; an account that has never set one still needs the menu to say
  // something, and the email below it identifies them regardless.
  const displayName = $derived(session.current?.account.displayName ?? null)

  // Signed out, the bar offers the way in — except on the sign-in page, where it would be a button to
  // the page you are already reading.
  const showSignIn = $derived(!session.signedIn && router.route.name !== 'sign-in')

  $effect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (root && !root.contains(event.target as Node)) open = false
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') open = false
    }
    document.addEventListener('click', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('click', close)
      document.removeEventListener('keydown', escape)
    }
  })

  async function signOut() {
    open = false
    await api.signOut()
    session.clear()
    router.requireSignIn()
  }
</script>

<header class="appbar">
  <div class="appbar-in">
    <a class="brand" href="/">
      <img
        src="/logo-44.png"
        srcset="/logo-44.png 1x, /logo-96.png 2x"
        width="36"
        height="36"
        alt=""
        fetchpriority="high"
        decoding="sync"
      />
      <span class="brand-title">Bird Journal <span class="brand-accent">Account</span></span>
    </a>

    <a class="site-link rip" href={config().marketing} data-external>
      birdjournal.com
      <span class="ico">{@html icons.external}</span>
    </a>

    <div class="end" bind:this={root}>
      {#if session.signedIn}
        <button
          class="avatar-btn rip"
          aria-label="Account"
          aria-expanded={open}
          aria-haspopup="menu"
          onclick={() => (open = !open)}
        >
          <Avatar name={displayName} size="md" />
        </button>

        {#if open}
          <div class="menu" role="menu">
            <div class="menu-hd">
              <Avatar name={displayName} size="lg" />
              <!-- scoped to .who: an unscoped `.menu-hd span` also catches the avatar's own glyph -->
              <span class="who">
                <b>{displayName ?? YOUR_ACCOUNT_LABEL}</b>
                <span>{session.email}</span>
              </span>
            </div>
            <button class="rip" role="menuitem" onclick={signOut}>
              <span class="ic">{@html icons.signOut}</span> Sign out
            </button>
          </div>
        {/if}
      {:else if showSignIn}
        <a class="btn btn-tonal btn-sm rip" use:ripple href="/sign-in">Sign in</a>
      {/if}
    </div>
  </div>
</header>

<style>
  /*
   * Sticky and frosted, matching the blog's header and the support site's exactly: same 0.82 white, same
   * saturate(1.4) blur(10px), same top: 0. The sites are linked from each other, so they should agree
   * while scrolling and not only at rest.
   *
   * z-index 50 is theirs too. The account menu is a CHILD of the bar, so it rides above without competing.
   */
  .appbar {
    position: sticky;
    top: 0;
    z-index: 50;
    background: var(--bj-surface-frosted);
    backdrop-filter: saturate(1.4) blur(10px);
    -webkit-backdrop-filter: saturate(1.4) blur(10px);
    border-bottom: 1px solid var(--bj-divider);
  }
  /* Where the blur is unsupported, 0.82 white over scrolling text is unreadable — so go opaque. */
  @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    .appbar {
      background: var(--bj-surface);
    }
  }
  /*
   * 69px + the 1px border = the blog's 70px, measured on the live site — and the support site's, which
   * reached the same number the same way. If the blog's header height ever changes, this follows it.
   */
  .appbar-in {
    max-width: var(--page-width);
    margin: 0 auto;
    padding: 0 var(--space-4);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: 69px;
  }

  /* Matches the blog's .brand exactly: 36px round logo, 700 weight, 1.1rem, tightened tracking. */
  .brand {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    /* It is a link home, so it takes the same 48px floor as everything else. */
    min-height: var(--touch);
    padding-right: var(--space-2);
    border-radius: var(--corner-full);
    color: var(--bj-on-surface);
    font-weight: 700;
    font-size: 1.1rem;
    letter-spacing: -0.01em;
    flex: none;
    margin-right: auto;
  }
  .brand:hover {
    text-decoration: none;
    color: var(--bj-on-surface);
  }
  .brand img {
    display: block;
    border-radius: 50%;
    flex: none;
  }
  /* "Bird Journal Account", with the second word blue — the blog's own .brand__accent treatment, and
     the same shape as the support site's "Bird Journal Support". */
  .brand-accent {
    color: var(--bj-primary);
  }
  @media (max-width: 480px) {
    /* The wordmark goes first; the logo alone still reads as home. */
    .brand-title {
      display: none;
    }
    /* Without the wordmark the link collapses to the 36px logo, which is under the touch floor. */
    .brand {
      min-width: var(--touch);
      justify-content: center;
      padding-right: 0;
    }
  }

  /* The way out to birdjournal.com, borrowed wholesale from the blog's header: bare hostname, small
     arrow, muted until hovered. One escape hatch, in the same words, on every site in the estate. */
  .site-link {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-height: var(--touch);
    padding: 0 var(--space-2);
    border-radius: var(--corner-full);
    font-size: 0.9rem;
    font-weight: 500;
    flex: none;
    color: var(--bj-on-surface-variant);
  }
  .site-link:hover {
    text-decoration: none;
    color: var(--bj-primary);
  }
  .site-link .ico {
    display: flex;
    opacity: 0.8;
  }
  /* First to go when the bar is short of room — nothing else here has a second home, and this one is
     reachable from every page's content anyway. */
  @media (max-width: 30rem) {
    .site-link {
      display: none;
    }
  }

  .end {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-2);
    min-height: var(--touch);
    /*
     * Held open whichever control is in it, so the bar does not change shape between the sign-in page
     * and the pages behind it. The support site needs a script before first paint to achieve this
     * because its bar is server-rendered and the session resolves later; here the session is read from
     * storage synchronously before the first render, so the width is all that is needed.
     */
    min-width: var(--touch);
  }

  /* The avatar IS the button — a circle inside a concentric circle, no outlined pill around it. */
  .avatar-btn {
    width: var(--touch);
    height: var(--touch);
    border-radius: 50%;
    border: 0;
    background: none;
    padding: 0;
    display: grid;
    place-items: center;
    cursor: pointer;
    color: var(--bj-primary);
  }

  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 0.35rem);
    min-width: 16rem;
    background: var(--bj-surface);
    border-radius: var(--corner-md);
    box-shadow: var(--elev-3);
    padding: var(--space-2);
    z-index: 50;
    animation: menu-in 0.13s cubic-bezier(0, 0, 0.2, 1);
  }
  @keyframes menu-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .menu {
      animation: none;
    }
  }
  .menu-hd {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--bj-divider);
    margin-bottom: var(--space-2);
  }
  .who {
    min-width: 0;
  }
  .who b {
    display: block;
    font-size: 0.94rem;
    font-weight: 500;
  }
  .who span {
    display: block;
    font-size: 0.82rem;
    color: var(--bj-on-surface-variant);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .menu button {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    width: 100%;
    min-height: var(--touch);
    padding: 0 var(--space-4);
    border-radius: var(--corner-full);
    border: 0;
    background: none;
    cursor: pointer;
    color: var(--bj-on-surface);
    font-size: 0.94rem;
    text-align: left;
  }
  .menu .ic {
    display: flex;
    color: var(--bj-on-surface-variant);
    flex: none;
  }
</style>
